import AtSymbolIcon from '@heroicons/react/outline/esm/AtSymbolIcon.js'
import CalendarIcon from '@heroicons/react/outline/esm/CalendarIcon.js'
import CheckCircleIcon from '@heroicons/react/outline/esm/CheckCircleIcon.js'
import CheckIcon from '@heroicons/react/outline/esm/CheckIcon.js'
import ClockIcon from '@heroicons/react/outline/esm/ClockIcon.js'
import DocumentTextIcon from '@heroicons/react/outline/esm/DocumentTextIcon.js'
import ExternalLinkIcon from '@heroicons/react/outline/esm/ExternalLinkIcon.js'
import HashtagIcon from '@heroicons/react/outline/esm/HashtagIcon.js'
import LinkIcon from '@heroicons/react/outline/esm/LinkIcon.js'
import MenuAlt1Icon from '@heroicons/react/outline/esm/MenuAlt1Icon.js'
import MenuAlt2Icon from '@heroicons/react/outline/esm/MenuAlt2Icon.js'
import PaperClipIcon from '@heroicons/react/outline/esm/PaperClipIcon.js'
import PhotographIcon from '@heroicons/react/outline/esm/PhotographIcon.js'
import SelectorIcon from '@heroicons/react/outline/esm/SelectorIcon.js'
import StarIcon from '@heroicons/react/outline/esm/StarIcon.js'
import TagIcon from '@heroicons/react/outline/esm/TagIcon.js'
import UserIcon from '@heroicons/react/outline/esm/UserIcon.js'
import UsersIcon from '@heroicons/react/outline/esm/UsersIcon.js'
import ViewGridIcon from '@heroicons/react/outline/esm/ViewGridIcon.js'
import { clsx } from 'clsx'

import {
  CustomFieldPrivacyOptions,
  CustomFieldSchema,
  CustomFieldType,
  Member,
  Post,
  Space,
  SpaceRoleType,
} from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'

import { Image } from '../common/components/Image.js'

export const TITLE_FIELD_KEY = 'title'
export const CONTENT_FIELD_KEY = 'content'
export const TRUNCATE_SCROLL_OFFSET = '-9rem'

export enum CustomFieldSubtype {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  DATETIME = 'datetime',
  EMAIL = 'email',
  HTML = 'html',
  MULTISELECT = 'multiselect',
  NUMBER = 'number',
  SELECT = 'select',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  TOGGLE = 'toggle',
  URL = 'url',
  RATING = 'rating',
  IMAGE = 'image',
  FILE = 'file',
  MEMBER = 'member',
  TAG = 'tag',
  SPACE = 'space',
  POST = 'post',
  IMAGES = 'images',
  FILES = 'files',
  MEMBERS = 'members',
  TAGS = 'tags',
  SPACES = 'spaces',
  POSTS = 'posts',
}

export enum CustomFieldViews {
  Text = 'Text',
  MultiLineText = 'MultiLineText',
  Title = 'Title',
  Button = 'Button',
  CommaSeparatedText = 'CommaSeparatedText',
  Email = 'Email',
  Phone = 'Phone',
  Url = 'Url',
  Html = 'Html',
  Number = 'Number',
  Rating = 'Rating',
  Date = 'Date',
  Check = 'Check',
  Pills = 'Pills',
  Pill = 'Pill',
  Location = 'Location',
  SocialMedia = 'SocialMedia',
  CoverImage = 'CoverImage',
  Gallery = 'Gallery',
}

export type CustomFieldViewProps = {
  subtype?: CustomFieldSubtype
  value: any
  field?: CustomFieldSchema
  entity?: any
}

export type TitleSizes = 'sm' | 'md' | 'lg'

export const getFieldSetting = (
  field: Partial<CustomFieldSchema>,
  key: string,
) => {
  const value = field?.settings?.find(setting => setting.key === key)?.value
  if (!value) {
    return null
  }
  try {
    return JSON.parse(value)
  } catch (err) {
    console.error('Cannot parse value in custom field')
    return null
  }
}

const getNewSettings = (
  field: CustomFieldSchema,
  key: string,
  value: string,
) => {
  const currentSettings = field?.settings ?? []
  const isNewKey = !field?.settings?.some(setting => setting.key === key)

  if (isNewKey) {
    return [...currentSettings, { key, value }]
  }

  return currentSettings.map(setting => {
    if (setting.key === key) {
      return { key, value }
    }

    return setting
  })
}

export const setFieldSetting = (
  field: CustomFieldSchema,
  key: string,
  value,
) => {
  const newValue = JSON.stringify(value)
  const newSettings = getNewSettings(field, key, newValue)

  return {
    ...field,
    settings: newSettings,
  } as CustomFieldSchema
}

export const getFieldIcon = field => {
  const fieldIcons = {
    [CustomFieldType.date]: <CalendarIcon />,
    [CustomFieldType.number]: <HashtagIcon />,
    [CustomFieldType.boolean]: <CheckCircleIcon />,
    [CustomFieldType.richText]: <DocumentTextIcon />,
    [CustomFieldType.relation]: <ExternalLinkIcon />,
    [CustomFieldSubtype.HTML]: <DocumentTextIcon />,
    [CustomFieldSubtype.TEXTAREA]: <MenuAlt1Icon />,
    [CustomFieldSubtype.URL]: <LinkIcon />,
    [CustomFieldSubtype.SELECT]: <SelectorIcon />,
    [CustomFieldSubtype.EMAIL]: <AtSymbolIcon />,
    [CustomFieldSubtype.CHECKBOX]: <CheckIcon />,
    [CustomFieldSubtype.TOGGLE]: <CheckCircleIcon />,
    [CustomFieldSubtype.DATETIME]: <ClockIcon />,
    [CustomFieldSubtype.RATING]: <StarIcon />,
    [CustomFieldSubtype.IMAGE]: <PhotographIcon />,
    [CustomFieldSubtype.IMAGES]: <PhotographIcon />,
    [CustomFieldSubtype.FILE]: <PaperClipIcon />,
    [CustomFieldSubtype.FILES]: <PaperClipIcon />,
    [CustomFieldSubtype.MEMBER]: <UserIcon />,
    [CustomFieldSubtype.MEMBERS]: <UsersIcon />,
    [CustomFieldSubtype.TAG]: <TagIcon />,
    [CustomFieldSubtype.TAGS]: <TagIcon />,
    [CustomFieldSubtype.POST]: <DocumentTextIcon />,
    [CustomFieldSubtype.POSTS]: <DocumentTextIcon />,
    [CustomFieldSubtype.SPACE]: <ViewGridIcon />,
    [CustomFieldSubtype.SPACES]: <ViewGridIcon />,
  }

  const icon = getFieldSetting(field, 'icon')
  if (icon) {
    return (
      <Image
        src={`/images/icons/${icon}.svg`}
        alt={field?.name}
        className="w-full h-full"
      />
    )
  }

  const key = field?.subtype || getFieldSetting(field, 'subtype') || field?.type

  return fieldIcons[key] || <MenuAlt2Icon />
}

export const useFieldPermissions = (space: Space) => {
  const { isModerator, data: member } = useAuthMember()
  const isSpaceAdmin =
    space?.authMemberProps?.spaceMember?.role?.type === SpaceRoleType.admin

  return {
    canUpdateField: ({
      field,
      entity,
    }: {
      field: CustomFieldSchema
      entity: Post | Member
    }) => {
      if (isSpaceAdmin || isModerator || !field?.writePrivacy?.allow) {
        return true
      }

      const owner = (entity as Post)?.owner?.member || (entity as Member)

      if (
        field?.writePrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.OWN) !==
        -1
      ) {
        if (owner?.id === member?.id || !entity?.id) {
          return true
        }
      }

      return false
    },
    canViewField: ({
      field,
      entity,
    }: {
      field: CustomFieldSchema
      entity: Post | Member
    }) => {
      if (isSpaceAdmin || isModerator || !field?.readPrivacy?.allow) {
        return true
      }

      const owner = (entity as Post)?.owner?.member || (entity as Member)

      if (
        field?.readPrivacy?.allow?.indexOf(CustomFieldPrivacyOptions.OWN) !== -1
      ) {
        if (owner?.id === member?.id || !entity?.id) {
          return true
        }
      }

      return false
    },
  }
}

export const getTitleClassName = (size: TitleSizes) => {
  return clsx(
    size === 'sm' && 'text-lg font-semibold',
    size === 'md' && 'text-xl',
    size === 'lg' && 'text-2xl',
  )
}
